import { Fragment } from 'react';

import type { Location } from 'react-router';

import type { LinkProps } from '@change/ds-core-ui';
import { Link } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import type { HtmlParserReplaceFn, HtmlTransformer } from '@change-corgi/core/react/html';
import { Link as RouterLink } from '@change-corgi/core/react/router';
import type { UtilityContext } from '@change-corgi/core/react/utilityContext';

import { isSamePageHref } from './shared/isSamePageHref';
import { isValidHref } from './shared/isValidHref';
import { processIframeSrc } from './shared/processIframeSrc';

export type ReplaceTagsOptions = {
	onClickLink?: () => void;
	linkVariant?: LinkProps['variant'];
	expressive?: boolean;
	stripLinks?: boolean;
	/**
	 * for security concerns, hash links are by default handled as normal link that will result in a normal navigation
	 *
	 * this can be overridden for trusted content to navigate to the target on the current page
	 */
	supportHashLinks?: boolean;
	/**
	 * for security concerns, links are open in a new window or tab (_blank)
	 *
	 * this can be overridden for trusted content to open them in the current window or tab
	 */
	linksTarget?: '_blank' | '_self';
	/**
	 * transforms the href of links found in the html
	 *
	 * can be useful to process links, for instance to remove the www.change.org domain
	 */
	transformLinkHref?: (href: string) => string;
	reportBadLinks?: boolean;
	reportParams?: Record<string, string | undefined | number>;
	transformers?: HtmlTransformer[];
	/**
	 * By default, only some tags are allowed
	 *
	 * if this is true, the default restrictions of dompurify will apply
	 */
	noRestrictedTags?: boolean;
	/**
	 * By default, only some attributes are allowed
	 *
	 * if this is true, the default restrictions of dompurify will apply
	 */
	noRestrictedAttrs?: boolean;
	/**
	 * By default, some styles are applied to content (e.g. ul/ol, iframes, video/img, ...)
	 *
	 * if this is true, those styles will not be applied
	 */
	noContentStyles?: boolean;
	/**
	 * By default, iframes are not allowed
	 */
	allowIframes?: boolean;
	className?: string;
	breakWord?: boolean;
};

// Warning:
// Styles need to be generated outside of the genReplaceTags function for consistent class generation between server and client (for SSR)
// For that reason, do not use `sx` inside this function.
// Instead, define a wrapping component (using another component or styled()) outside of the function, then use that component inside the function.
export function genReplaceTags(
	{
		onClickLink,
		linkVariant,
		expressive,
		stripLinks,
		reportBadLinks,
		supportHashLinks,
		linksTarget,
		reportParams,
		transformLinkHref,
	}: ReplaceTagsOptions,
	location: Location,
	utilityContext: UtilityContext,
): HtmlParserReplaceFn {
	// oxlint-disable-next-line complexity, max-statements
	return function replaceTags({ name, attribs, children }, { domToReact }) {
		if (name === 'a') {
			const { href: hrefOption, class: className, style, ...restAttribs } = attribs;

			const href =
				transformLinkHref && !!hrefOption && isValidHref(hrefOption) ? transformLinkHref(hrefOption) : hrefOption;
			const badLink = !href || !isValidHref(href);
			if (badLink && reportBadLinks) {
				void utilityContext.errorReporter.report({
					error: 'Invalid href in parsed HTML',
					params: {
						href: href || '(no href)',
						...reportParams,
					},
				});
			}
			if (stripLinks || badLink) {
				return <span>{domToReact(children)}</span>;
			}
			if (supportHashLinks && isSamePageHref(href, location, utilityContext)) {
				// there is what could be a bug in react-router where hash links ignore the current query params
				const to = href.startsWith('#') ? `${location.pathname}${location.search}${href}` : href;
				return (
					<Link
						as={RouterLink}
						variant={linkVariant}
						expressive={expressive}
						to={to}
						className={cn('wrap-anywhere', className)}
						{...restAttribs}
						onClick={onClickLink}
						preventScrollReset
					>
						{domToReact(children)}
					</Link>
				);
			}

			return (
				<Link
					as={RouterLink}
					variant={linkVariant}
					expressive={expressive}
					to={href}
					target={linksTarget || '_blank'}
					className={cn('wrap-anywhere', className)}
					{...restAttribs}
					onClick={onClickLink}
					forceMode={href.includes('#') ? 'external' : undefined}
				>
					{domToReact(children)}
				</Link>
			);
		}
		if (name === 'iframe') {
			const src = processIframeSrc(attribs.src);
			if (!src) {
				// oxlint-disable-next-line react/jsx-no-useless-fragment, react/jsx-fragments
				return <Fragment />;
			}
			// oxlint-disable-next-line no-param-reassign
			attribs.src = src;
		}

		return undefined;
	};
}
